import authService from './authService';

const AGENT_PARENT_API_URL = process.env.REACT_APP_AGENT_PARENT_API_URL || 'https://your-agent-parent-api-url.execute-api.us-east-1.amazonaws.com/prod';

/**
 * Tool Approvals Service - Manages tool approval settings for users
 * 
 * Approval statuses:
 * - 'auto_approve': Tool will be executed automatically without user confirmation
 * - 'require_approval': User will be prompted to approve/deny each execution (default for new tools)
 * - 'always_deny': Tool will never be executed
 */
class ToolApprovalsService {
  /**
   * Get the ID token for API calls
   */
  async getIdToken() {
    return await authService.getIdToken();
  }

  /**
   * Get all tool approvals for the current user
   * @returns {Promise<Array>} Array of tool approval settings
   */
  async getToolApprovals() {
    try {
      const token = await this.getIdToken();
      const response = await fetch(`${AGENT_PARENT_API_URL}/tool-approvals`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get tool approvals: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data.toolApprovals || [];
    } catch (error) {
      console.error('Error getting tool approvals:', error);
      throw error;
    }
  }

  /**
   * Save or update a tool approval setting
   * @param {string} toolName - The name of the tool
   * @param {string} approvalStatus - One of: 'auto_approve', 'require_approval', 'always_deny'
   * @param {string} description - Optional description of the tool
   * @returns {Promise<Object>} The saved tool approval
   */
  async putToolApproval(toolName, approvalStatus, description = '') {
    try {
      const token = await this.getIdToken();
      const response = await fetch(`${AGENT_PARENT_API_URL}/tool-approvals`, {
        method: 'PUT',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          toolName,
          approvalStatus,
          description,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save tool approval: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving tool approval:', error);
      throw error;
    }
  }

  /**
   * Delete a tool approval setting (reverts to default 'require_approval')
   * @param {string} toolName - The name of the tool to delete approval for
   * @returns {Promise<Object>} The deletion result
   */
  async deleteToolApproval(toolName) {
    try {
      const token = await this.getIdToken();
      const response = await fetch(`${AGENT_PARENT_API_URL}/tool-approvals?toolName=${encodeURIComponent(toolName)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete tool approval: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting tool approval:', error);
      throw error;
    }
  }

  /**
   * Get the approval status for a specific tool
   * If no setting exists, returns 'require_approval' (default for new tools)
   * @param {string} toolName - The name of the tool
   * @param {Array} toolApprovals - Optional cached list of approvals to search
   * @returns {Promise<string>} The approval status
   */
  async getToolApprovalStatus(toolName, toolApprovals = null) {
    try {
      const approvals = toolApprovals || await this.getToolApprovals();
      const approval = approvals.find(a => a.toolName === toolName);
      return approval ? approval.approvalStatus : 'require_approval'; // Default to require approval
    } catch (error) {
      console.error('Error getting tool approval status:', error);
      return 'require_approval'; // Default to require approval on error
    }
  }

  /**
   * Check if a tool should be auto-approved
   * @param {string} toolName - The name of the tool
   * @param {Array} toolApprovals - Optional cached list of approvals to search
   * @returns {Promise<boolean>} True if the tool should be auto-approved
   */
  async isToolAutoApproved(toolName, toolApprovals = null) {
    const status = await this.getToolApprovalStatus(toolName, toolApprovals);
    return status === 'auto_approve';
  }

  /**
   * Check if a tool should always be denied
   * @param {string} toolName - The name of the tool
   * @param {Array} toolApprovals - Optional cached list of approvals to search
   * @returns {Promise<boolean>} True if the tool should always be denied
   */
  async isToolAlwaysDenied(toolName, toolApprovals = null) {
    const status = await this.getToolApprovalStatus(toolName, toolApprovals);
    return status === 'always_deny';
  }

  /**
   * Check if a tool requires approval
   * @param {string} toolName - The name of the tool
   * @param {Array} toolApprovals - Optional cached list of approvals to search
   * @returns {Promise<boolean>} True if the tool requires approval
   */
  async isToolRequiresApproval(toolName, toolApprovals = null) {
    const status = await this.getToolApprovalStatus(toolName, toolApprovals);
    return status === 'require_approval';
  }

  /**
   * Batch update tool approvals for multiple tools
   * @param {Array<{toolName: string, approvalStatus: string, description?: string}>} approvals
   * @returns {Promise<Array>} Array of results
   */
  async batchUpdateToolApprovals(approvals) {
    const results = await Promise.all(
      approvals.map(({ toolName, approvalStatus, description }) =>
        this.putToolApproval(toolName, approvalStatus, description)
      )
    );
    return results;
  }
}

export default new ToolApprovalsService();
